import { lazy } from "react";
import { ModuleConfig } from "../../types/ModuleConfig";

const OutgoingComms = lazy(() => import("./OutgoingComms"));
const CommsLogSettings = lazy(() => import("./CommsLogSettings"));

/**
 * Comms Log — provider-truth activity log of every outgoing SMS + email this tenant
 * sends (Cellcast + Postmark, queried with the tenant's own keys). Built 2026-08-04 so
 * managers can confirm what the system sends automatically.
 *
 * `id` MUST equal the backend permissions moduleId ('comms-log') or the navbar silently
 * hides the module.
 */
const commsLogModuleConfig: ModuleConfig = {
  id: "comms-log",
  name: "Outgoing Comms",
  description: "See every SMS and email the system sends — straight from the providers' own records",
  version: "1.3.0",
  enabled: true,
  category: "communication",
  order: 46,
  icon: "📤",

  routes: [
    {
      path: "/comms-log",
      component: OutgoingComms,
      permissionLevel: "view",
      useLayout: true,
      // A list page whose main content is a table takes the full page width (Data Tables rule 7.0).
      layoutOptions: { maxW: "100%" },
    },
    {
      path: "/comms-log/settings",
      component: CommsLogSettings,
      permissionLevel: "edit",
      useLayout: true,
      layoutOptions: { maxW: "container.md" },
    },
  ],

  navigation: [
    {
      label: "Outgoing Comms",
      path: "/comms-log",
      icon: "📤",
      permissionLevel: "view",
    },
    {
      label: "Comms Settings",
      path: "/comms-log/settings",
      icon: "⚙️",
      permissionLevel: "edit",
    },
  ],

  // Replaced at runtime by ModulePermissionsContext (backend SSOT).
  permissions: {
    view: [],
    create: [],
    edit: [],
    delete: [],
  },

  quickActions: [],

  changelog: [
    {
      version: "1.3.0",
      releaseDate: "2026-10-20",
      title: "Read every message in full",
      features: [
        "✨ Click any message to read it in full — the whole email as it was sent, or the whole text message",
        "🔒 Sign-in links are hidden completely and one-time codes show as xx, in the list and in the full message",
        "🎨 The message list now uses the full width of the screen, with larger, easier-to-read text",
      ],
    },
    {
      version: "1.3.0",
      releaseDate: "2026-10-18",
      title: "Who each message came from",
      features: [
        "✨ A From column: the number or name each text was sent from, and the address each email came from",
        "🧹 The page no longer talks about other sites — it simply shows this site's own messages",
        "🔒 How a site's messages are kept separate is now a platform-admin setting only",
      ],
    },
    {
      version: "1.1.0",
      releaseDate: "2026-10-16",
      title: "Only this site's messages — and a clearer page",
      features: [
        "🔒 Every message is matched to the site that sent it; other sites' texts and emails on a shared account are kept off the page and counted",
        "✨ Each message says why it's on this site's page",
        "✨ Comms Settings explains how this site's messages are kept separate; platform admins can link a site's SMS app",
        "🎨 Larger, easier-to-read design; settings are edited where you read them",
      ],
    },
    {
      version: "1.1.0",
      releaseDate: "2026-08-04",
      title: "Outgoing comms activity log",
      features: [
        "✨ Unified outgoing SMS + email log, queried live from Cellcast and Postmark with the tenant's own keys",
        "✨ Daily and weekly summary emails to chosen recipients",
      ],
    },
  ],

  roadmap: [],
};

export default commsLogModuleConfig;
